import React, { forwardRef, useRef } from 'react';
import Textarea from 'react-textarea-autosize';
import { Label } from '../';
import { ValidationMessage } from '../ValidationMessage';

/**
 * An auto-resizing textarea with an optional floating label.
 *
 * Renders a `react-textarea-autosize` textarea wrapped in a labelled container.
 * All props not listed below are forwarded directly to the underlying textarea element.
 *
 * @param {object} props
 * @param {string} [props.label] - Label text rendered above the textarea.
 * @param {string} [props.name] - `name` attribute on the textarea; also used as the label's `htmlFor`.
 * @param {string} [props.value] - Controlled value of the textarea.
 * @param {function} [props.onChange] - Change handler — receives the native textarea event.
 * @param {number} [props.minRows=4] - Minimum number of visible rows before auto-grow kicks in.
 * @param {number} [props.maxRows] - Maximum number of rows the textarea will grow to.
 * @param {boolean} [props.disabled=false] - Disables the textarea and dims the container.
 * @param {string} [props.placeholder] - Placeholder text shown when the textarea is empty.
 * @param {string} [props.title] - Tooltip text shown on the label.
 * @param {boolean} [props.isOptional=false] - When true, renders an "optional" badge next to the label.
 * @param {{ valid: boolean|string, message: string }} [props.validation] - Inline validation, shown
 *   under the field and marking it as in error, as Input and TextareaCode do.
 * @param {React.Ref} ref - Forwarded ref attached to the underlying textarea element.
 * @returns {React.ReactElement}
 */
const _TextareaAutosize = (props, ref) => {
  const tooltip = props.title;
  const label = props.label;
  const name = props.name;
  const disabled = props.disabled;
  const isOptional = props.isOptional || false;
  const validation = props.validation;

  const internalRef = useRef();
  const textareaRef = ref || internalRef;
  let newProps = { ...props, minRows: props.minRows || 4 };
  props.title && delete newProps.title;
  props.label && delete newProps.label;
  props.isOptional !== undefined && delete newProps.isOptional;
  props.validation !== undefined && delete newProps.validation;
  return (
    <div
      className={
        'TextareaAutosize' +
        (disabled ? ' disabled' : '') +
        (validation && !validation.valid ? ' has-error' : '')
      }
    >
      <div className="textarea-content" onClick={() => textareaRef.current.focus()}>
        <Label
          label={label}
          htmlFor={name}
          title={tooltip}
          disabled={disabled}
          isOptional={isOptional}
        />
        <Textarea {...newProps} ref={textareaRef} />
      </div>
      <ValidationMessage validation={validation} />
    </div>
  );
};

export const TextareaAutosize = forwardRef(_TextareaAutosize);
